"use client";

import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu } from "lucide-react";
import Link from "next/link";
import React from "react";
import { cls, cn } from "@/lib/utils";
import { Button } from "../button";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "../drawer";
import {
  mobileButtonMotionProperties,
  mobileItemElementMotionProperties,
  mobileWrapperElementMotionProperties,
} from "./animations";
import { NAVIGATION_ITEMS, type NavigationItem } from "./consts";

export default function MobileNavigation() {
  const [open, setOpen] = React.useState(false);
  const [openSection, setOpenSection] = React.useState<string | null>(null);

  const handleOnOpenChange = React.useCallback((value: boolean) => {
    setOpen(value);
  }, []);

  const handleOnClick = React.useCallback(() => {
    setOpen(prev => !prev);
  }, []);

  const handleOnInteractOutside = React.useCallback(() => {
    setOpen(false);
  }, []);

  const handleNavigate = React.useCallback(() => {
    setOpen(false);
    setOpenSection(null);
  }, []);

  const toggleSection = React.useCallback((label: string) => {
    setOpenSection(prev => (prev === label ? null : label));
  }, []);

  // Filter out Request Consultation from the main list
  const navItems = NAVIGATION_ITEMS.filter(
    item => item.label !== "Request Consultation",
  );

  return (
    <Drawer open={open} direction="right" onOpenChange={handleOnOpenChange}>
      <DrawerTrigger asChild>
        <motion.div {...mobileButtonMotionProperties}>
          <Button
            aria-label="Open the menu"
            variant="ghost"
            size="icon"
            className="text-primary-foreground hover:bg-primary-foreground/10"
            onClick={handleOnClick}
          >
            <Menu />
          </Button>
        </motion.div>
      </DrawerTrigger>

      <DrawerContent
        className={cls`p-5 gap-4 h-full w-[75vw] max-w-sm inset-x-[unset] right-0`}
        onInteractOutside={handleOnInteractOutside}
      >
        <DrawerHeader>
          <DrawerTitle>Cornerstone Homes</DrawerTitle>
        </DrawerHeader>

        <motion.div
          className={cls`flex flex-col gap-1 flex-1 overflow-y-auto`}
          {...mobileWrapperElementMotionProperties}
        >
          {navItems.map(item => (
            <MobileNavItem
              key={item.label}
              item={item}
              isOpen={openSection === item.label}
              onToggle={toggleSection}
              onNavigate={handleNavigate}
            />
          ))}
        </motion.div>

        <DrawerFooter>
          <Button asChild size="lg" className="w-full">
            <Link href="/request-consultation" onClick={handleNavigate}>
              Request A Consultation
            </Link>
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

function MobileNavItem({
  item,
  isOpen,
  onToggle,
  onNavigate,
}: {
  item: NavigationItem;
  isOpen: boolean;
  onToggle: (label: string) => void;
  onNavigate: () => void;
}) {
  const hasChildren = !!item.children;

  if (!hasChildren) {
    return (
      <motion.div {...mobileItemElementMotionProperties}>
        <Link
          href={item.href as string}
          onClick={onNavigate}
          className="flex items-center rounded-md px-3 py-2.5 text-sm font-medium hover:bg-accent transition-colors"
        >
          {item.label}
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div {...mobileItemElementMotionProperties}>
      <button
        type="button"
        onClick={() => onToggle(item.label)}
        className="flex w-full items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium hover:bg-accent transition-colors"
      >
        {item.label}
        <ChevronDown
          className={cn(
            "h-4 w-4 transition-transform duration-200",
            isOpen && "rotate-180",
          )}
        />
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="ml-3 flex flex-col gap-0.5 border-l pl-3 py-1">
              {item.children?.map(child => (
                <Link
                  key={child.label}
                  href={child.href as string}
                  onClick={onNavigate}
                  className="block rounded-md px-3 py-2 hover:bg-accent transition-colors"
                >
                  <span className="text-sm font-medium">{child.label}</span>
                  {child.description && (
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {child.description}
                    </span>
                  )}
                </Link>
              ))}

              {item.viewAllHref && (
                <Link
                  href={item.viewAllHref}
                  onClick={onNavigate}
                  className="mt-1 block rounded-md border-t px-3 py-2 text-sm font-medium text-primary hover:bg-accent transition-colors"
                >
                  {item.viewAllLabel ?? "View All"} &rarr;
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
